// SponsorshipCalendar.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  memo,
} from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Calendar,
  Users,
  Filter,
  Rss,
  MapPin,
  Globe2,
} from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo/BrandLogo";
import { getAllEvents } from "@/Services/EventServices";

// ---------------------------------------------------------------------------
// CONSTANTS — module-scope, frozen
// ---------------------------------------------------------------------------

const BACKEND = Object.freeze(
  process.env.REACT_APP_BACKEND_URL || window.location.origin,
);

// Derived dynamically from API data — these are fallback display tags only
const MEETING_TYPES = Object.freeze(["Virtual", "In person", "Hybrid"]);
const ENTRY_TYPES = Object.freeze(["Free", "Invitation Only", "Paid"]);

const BUCKET_ORDER = Object.freeze([
  "Today & tomorrow",
  "This week",
  "Next 30 days",
  "Later",
]);

// ---------------------------------------------------------------------------
// PURE UTILITY FUNCTIONS — module-scope
// ---------------------------------------------------------------------------

const gmtLabelToOffsetMinutes = (tz) => {
  const s = String(tz || "")
    .replace(/[()]/g, "")
    .trim();
  const m = s.match(/GMT\s*([+-])\s*(\d{1,2})(?::(\d{2}))?/i);
  if (!m) return null;
  const sign = m[1] === "-" ? -1 : 1;
  return sign * (Number(m[2] || 0) * 60 + Number(m[3] || 0));
};

const isoFromParts = (dateStr, timeStr, tzLabel) => {
  if (!dateStr) return "";
  const t = timeStr || "00:00";
  const mins = gmtLabelToOffsetMinutes(tzLabel);
  if (mins == null) return `${dateStr}T${t}:00Z`;
  const sign = mins < 0 ? "-" : "+";
  const abs = Math.abs(mins);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `${dateStr}T${t}:00${sign}${hh}:${mm}`;
};

const stripHtml = (html) =>
  html
    ? String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";

const fmtDateLong = (isoStr, tz) => {
  try {
    if (!isoStr) return "TBA";
    const d = new Date(isoStr);
    if (!Number.isFinite(d.getTime())) return "TBA";
    const opts = {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    const offsetMins = gmtLabelToOffsetMinutes(tz);
    if (offsetMins != null) {
      return new Date(d.getTime() + offsetMins * 60_000).toLocaleDateString(
        undefined,
        { ...opts, timeZone: "UTC" },
      );
    }
    return d.toLocaleDateString(undefined, {
      ...opts,
      timeZone: tz || undefined,
    });
  } catch {
    return "TBA";
  }
};

const fmtTimeRange = (startIso, endIso, tz) => {
  try {
    if (!startIso) return "";
    const offsetMins = gmtLabelToOffsetMinutes(tz);
    const fmt = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      if (!Number.isFinite(d.getTime())) return "";
      if (offsetMins != null) {
        return new Date(d.getTime() + offsetMins * 60_000).toLocaleTimeString(
          undefined,
          {
            hour: "numeric",
            minute: "2-digit",
            timeZone: "UTC",
          },
        );
      }
      return d.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        timeZone: tz || undefined,
      });
    };
    const s = fmt(startIso);
    const e = endIso ? fmt(endIso) : "";
    return e ? `${s} – ${e}` : s;
  } catch {
    return "";
  }
};

const bucketFor = (isoStr) => {
  if (!isoStr) return "Later";
  const start = new Date(isoStr).getTime();
  const now = Date.now();
  const diffDays = (start - now) / 86_400_000;
  if (diffDays < 2) return "Today & tomorrow";
  if (diffDays < 7) return "This week";
  if (diffDays < 30) return "Next 30 days";
  return "Later";
};

// ---------------------------------------------------------------------------
// DATA NORMALIZER
// Maps the raw /get_event response to the shape CalendarCard expects.
//
// Key mappings from actual API response:
//   raw._id              → id
//   raw.eventName        → title
//   raw.sponcers[]       → sponsors / event_slot
//   raw.meetingType      → tags[0]
//   raw.entry            → tags[1]
//   raw.bookingCount     → registered
//   raw.venue            → venue
// ---------------------------------------------------------------------------

const normalizeEventForCalendar = (raw) => {
  const tz = raw.timezone || "(GMT+0)";
  const startTime = isoFromParts(raw.startDate, raw.startTime, tz);
  const endTime = isoFromParts(raw.endDate || raw.startDate, raw.endTime, tz);

  // Build tags from available categorical fields
  const tags = [raw.meetingType, raw.entry, raw.visibility]
    .filter(Boolean)
    .filter((t) => t !== "Local" && t !== "Global"); // skip visibility for display

  // Sponsors — API spells it "sponcers"
  const sponsors = Array.isArray(raw.sponcers) ? raw.sponcers : [];
  const leadSponsor = sponsors[0] || null;
  const additionalSponsors = sponsors.slice(1);

  // Event-level slot: "claimed" if any sponsor exists
  const eventSlot = {
    status: leadSponsor ? "claimed" : "open",
    lead_sponsor: leadSponsor
      ? {
          id: leadSponsor._id,
          company: leadSponsor.title,
          logo: leadSponsor.image,
          value_prop: stripHtml(leadSponsor.descp),
          website: leadSponsor.website || "",
          intro_video: leadSponsor.introVideo || "",
          bg_color: leadSponsor.bgcColor || "",
        }
      : null,
    additional_sponsors: additionalSponsors.map((s) => ({
      id: s._id,
      company: s.title,
      logo: s.image,
      value_prop: stripHtml(s.descp),
    })),
  };

  // Table slots: derived from sponsors (each sponsor can "claim" a table)
  // In this API, sponsors represent claimed table spots
  const tableSlots = sponsors.map((s) => ({
    table_id: s._id,
    circle: s.title,
    topic:
      stripHtml(s.descp).slice(0, 80) +
      (stripHtml(s.descp).length > 80 ? "…" : ""),
    status: "claimed",
    seats_left: 0,
    sponsor_name: s.title,
    sponsor_logo: s.image,
  }));

  // Determine additional open table slots
  // Since the API doesn't provide session tables here,
  // we show "open" slots equal to max(0, 4 - claimedCount) as available inventory
  const claimedCount = sponsors.length;
  const estimatedTotal = Math.max(claimedCount, 4); // at least show realistic total
  const openCount = Math.max(0, estimatedTotal - claimedCount);

  // Add open slot placeholders
  const allSlots = [...tableSlots];
  for (let i = 0; i < openCount; i++) {
    allSlots.push({
      table_id: `open-${i}`,
      circle: "Available",
      topic: "Open table slot — available to sponsor",
      status: "open",
      seats_left: 6,
    });
  }

  return {
    id: raw._id,
    title: raw.eventName || "Untitled Event",
    start_time: startTime,
    end_time: endTime,
    timezone: tz,
    venue: raw.venue || "",
    meetingType: raw.meetingType || "",
    entry: raw.entry || "",
    description: stripHtml(raw.description),
    tags,
    cover: raw.eventImage || "",
    registered: Number(raw.bookingCount ?? 0),
    isBooked: !!raw.isBooked,
    ticketPrice: Number(raw.ticketPrice ?? 0),
    currency: raw.currency || "USD",
    event_slot: eventSlot,
    tables_total: estimatedTotal,
    tables_open: openCount,
    table_slots: allSlots,
    // Raw sponsor list for display
    sponsors,
    hosts: raw.hosts || [],
    speakers: raw.speakers || [],
  };
};

// ---------------------------------------------------------------------------
// SKELETON
// ---------------------------------------------------------------------------

const CalendarCardSkeleton = memo(() => (
  <div
    aria-hidden="true"
    className="glass rounded-2xl p-6 flex flex-col gap-4 animate-pulse"
  >
    <div className="h-3 w-48 bg-white/[0.06] rounded" />
    <div className="h-7 w-3/4 bg-white/[0.06] rounded" />
    <div className="flex gap-1.5">
      <div className="h-4 w-16 bg-white/[0.04] rounded" />
      <div className="h-4 w-20 bg-white/[0.04] rounded" />
    </div>
    <div className="h-20 rounded-xl bg-white/[0.03] border border-white/[0.06]" />
    <div className="space-y-2">
      <div className="h-3 w-full bg-white/[0.04] rounded" />
      <div className="h-3 w-4/5 bg-white/[0.04] rounded" />
      <div className="h-3 w-3/4 bg-white/[0.04] rounded" />
    </div>
    <div className="pt-3 border-t border-white/[0.06] flex gap-3">
      <div className="h-3 w-20 bg-white/[0.04] rounded" />
      <div className="h-6 w-24 bg-white/[0.04] rounded-full ml-auto" />
    </div>
  </div>
));
CalendarCardSkeleton.displayName = "CalendarCardSkeleton";

// ---------------------------------------------------------------------------
// EMPTY STATE
// ---------------------------------------------------------------------------

const EmptyState = memo(({ onClear }) => (
  <div className="glass rounded-2xl p-10 text-center">
    <Sparkles className="text-[#C6A87C] mx-auto mb-3" size={20} />
    <p className="font-display text-2xl tracking-tight mb-2">
      No matching events.
    </p>
    <p className="text-white/75 text-sm mb-5">
      Try clearing the topic filter or switching to <em>All</em>.
    </p>
    <button
      type="button"
      onClick={onClear}
      className="px-4 py-2 rounded-full border border-[#C6A87C]/40 text-[#C6A87C] text-sm hover:bg-[#C6A87C]/10 transition-colors"
    >
      Clear filters
    </button>
  </div>
));
EmptyState.displayName = "EmptyState";

// ---------------------------------------------------------------------------
// ERROR STATE
// ---------------------------------------------------------------------------

const ErrorState = memo(({ message, onRetry }) => (
  <div className="glass rounded-2xl p-10 text-center">
    <p className="text-red-300 text-base mb-4">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      className="px-5 py-2.5 rounded-full bg-[#C6A87C]/10 border border-[#C6A87C]/40 text-[#C6A87C] text-sm hover:bg-[#C6A87C]/20 transition-colors"
    >
      Retry
    </button>
  </div>
));
ErrorState.displayName = "ErrorState";

// ---------------------------------------------------------------------------
// FILTER CONTROLS
// ---------------------------------------------------------------------------

const FilterControls = memo(
  ({
    filter,
    onFilterChange,
    tagFilter,
    onTagChange,
    openCount,
    totalCount,
    availableTags,
  }) => (
    <div
      className="mt-10 flex flex-wrap items-center gap-3"
      data-testid="calendar-filters"
    >
      <span className="overline flex items-center gap-1.5">
        <Filter size={11} aria-hidden="true" /> Filter
      </span>

      {/* Open / All toggle */}
      <div
        className="flex items-center gap-1 border border-white/15 rounded-full p-1"
        role="group"
        aria-label="Availability filter"
      >
        {[
          { key: "open", label: `Open slots · ${openCount}` },
          { key: "all", label: `All · ${totalCount}` },
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => onFilterChange(key)}
            aria-pressed={filter === key}
            data-testid={`filter-${key}`}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
              filter === key
                ? "bg-[#C6A87C] text-[#061412]"
                : "text-white/75 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tag filters — dynamically built from API data */}
      <div
        className="flex flex-wrap gap-1.5"
        role="group"
        aria-label="Category filter"
      >
        <button
          type="button"
          onClick={() => onTagChange(null)}
          aria-pressed={tagFilter === null}
          data-testid="filter-tag-all"
          className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
            tagFilter === null
              ? "bg-white/[0.08] border-white/30 text-white"
              : "border-white/10 text-white/55 hover:text-white"
          }`}
        >
          All categories
        </button>
        {availableTags.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTagChange(tagFilter === t ? null : t)}
            aria-pressed={tagFilter === t}
            data-testid={`filter-tag-${t}`}
            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
              tagFilter === t
                ? "bg-[#C6A87C]/15 border-[#C6A87C]/50 text-[#C6A87C]"
                : "border-white/10 text-white/55 hover:text-[#C6A87C]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  ),
);
FilterControls.displayName = "FilterControls";

// ---------------------------------------------------------------------------
// SUBSCRIBE PANEL
// ---------------------------------------------------------------------------

const SubscribePanel = memo(({ icsUrl, webcalUrl, onCopy }) => (
  <div
    className="mt-5 border border-white/[0.08] rounded-xl p-4"
    data-testid="calendar-subscribe-panel"
  >
    <p className="overline mb-2 flex items-center gap-1.5">
      <Rss size={11} aria-hidden="true" /> Subscribe in your calendar
    </p>
    <p className="text-xs text-white/70 leading-relaxed mb-3">
      Open sponsor slots appear directly in Google / Outlook / Apple Calendar.
      Auto-refreshes every hour.
    </p>
    <div className="flex flex-wrap gap-2">
      <a
        href={webcalUrl}
        data-testid="calendar-subscribe-webcal"
        className="text-xs px-3 py-1.5 rounded-full bg-[#C6A87C] text-[#061412] hover:bg-[#D9BC93] transition-colors font-medium inline-flex items-center gap-1.5"
      >
        <Calendar size={12} aria-hidden="true" /> Subscribe
      </a>
      <a
        href={icsUrl}
        download="vmeetio-sponsorship.ics"
        data-testid="calendar-subscribe-download"
        className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:border-[#C6A87C]/50 hover:text-[#C6A87C] transition-colors inline-flex items-center gap-1.5"
      >
        Download .ics
      </a>
      <button
        type="button"
        onClick={onCopy}
        data-testid="calendar-subscribe-copy"
        className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:border-[#C6A87C]/50 hover:text-[#C6A87C] transition-colors"
      >
        Copy link
      </button>
    </div>
  </div>
));
SubscribePanel.displayName = "SubscribePanel";

// ---------------------------------------------------------------------------
// MEETING TYPE BADGE
// ---------------------------------------------------------------------------

const MeetingTypeBadge = memo(({ type }) => {
  if (!type) return null;
  const colors = {
    Virtual: "border-blue-400/30 text-blue-300",
    "In person": "border-green-400/30 text-green-300",
    Hybrid: "border-purple-400/30 text-purple-300",
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
        colors[type] || "border-white/10 text-white/60"
      }`}
    >
      {type}
    </span>
  );
});
MeetingTypeBadge.displayName = "MeetingTypeBadge";

// ---------------------------------------------------------------------------
// CALENDAR CARD — memoised, one per event
// ---------------------------------------------------------------------------

const CalendarCard = memo(function CalendarCard({ event }) {
  const e = event;
  const claimed = e.event_slot.status === "claimed";
  const lead = e.event_slot.lead_sponsor;
  const additionalSponsors = e.event_slot.additional_sponsors || [];

  const dateStr = useMemo(
    () => fmtDateLong(e.start_time, e.timezone),
    [e.start_time, e.timezone],
  );

  const timeStr = useMemo(
    () => fmtTimeRange(e.start_time, e.end_time, e.timezone),
    [e.start_time, e.end_time, e.timezone],
  );

  const visibleSlots = useMemo(
    () => (e.table_slots || []).slice(0, 5),
    [e.table_slots],
  );

  const hasOpenTables = e.tables_open > 0;

  return (
    <article
      data-testid={`calendar-card-${e.id}`}
      className="glass rounded-2xl p-6 flex flex-col gap-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/20"
    >
      {/* Header */}
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <p className="text-xs text-white/65">{dateStr}</p>
            {timeStr && <p className="text-xs text-white/45">· {timeStr}</p>}
          </div>

          <Link to={`/events/${e.id}`} className="block">
            <h3 className="font-display text-2xl tracking-tight leading-tight hover:text-[#C6A87C] transition-colors">
              {e.title}
            </h3>
          </Link>

          {/* Location */}
          {e.venue && (
            <p className="mt-1.5 text-[11px] text-white/50 flex items-center gap-1">
              <MapPin size={10} aria-hidden="true" />
              <span className="truncate">{e.venue}</span>
            </p>
          )}

          {/* Tags */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {e.meetingType && <MeetingTypeBadge type={e.meetingType} />}
            {e.entry && e.entry !== "Invitation Only" && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#7BC8A6]/30 text-[#7BC8A6]">
                {e.entry}
              </span>
            )}
            {e.entry === "Invitation Only" && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#C6A87C]/30 text-[#C6A87C]">
                Invite Only
              </span>
            )}
          </div>
        </div>

        {/* Registered count */}
        {e.registered > 0 && (
          <div className="shrink-0 text-right">
            <div className="font-display text-2xl text-[#C6A87C]">
              {e.registered}
            </div>
            <div className="text-[10px] text-white/50 uppercase tracking-wider">
              registered
            </div>
          </div>
        )}
      </header>

      {/* Event sponsor slot */}
      <div
        className={`rounded-xl p-4 border ${
          claimed
            ? "border-white/[0.08] bg-white/[0.02]"
            : "border-dashed border-[#C6A87C]/45 bg-[#C6A87C]/[0.04]"
        }`}
        data-testid={`calendar-event-slot-${e.id}`}
      >
        <p className="overline mb-2 flex items-center gap-1.5 text-[10px]">
          <Sparkles size={10} aria-hidden="true" /> Lead sponsor
          <span
            className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] border ${
              claimed
                ? "border-white/15 text-white/55"
                : "border-[#C6A87C]/60 text-[#C6A87C]"
            }`}
          >
            {claimed ? "Claimed" : "Available"}
          </span>
        </p>

        {claimed && lead ? (
          <div className="flex items-start gap-3">
            <BrandLogo company={lead.company} logo={lead.logo} size={36} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white/90 truncate">
                {lead.company}
              </p>
              {lead.value_prop && (
                <p className="text-[11px] text-white/55 mt-0.5 line-clamp-2 leading-snug">
                  {lead.value_prop}
                </p>
              )}
            </div>
          </div>
        ) : (
          <Link
            to={`/sponsor?event=${encodeURIComponent(e.id)}`}
            data-testid={`calendar-claim-event-${e.id}`}
            className="text-xs font-medium text-[#C6A87C] hover:text-[#D9BC93] flex items-center gap-1.5 transition-colors"
          >
            Claim lead sponsorship <ArrowRight size={12} />
          </Link>
        )}
      </div>

      {/* Additional sponsors */}
      {additionalSponsors.length > 0 && (
        <div>
          <p className="overline mb-2 text-[10px] text-white/55">
            Also sponsored by
          </p>
          <div className="flex flex-wrap gap-2">
            {additionalSponsors.slice(0, 4).map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/[0.03] border border-white/[0.06]"
                title={s.company}
              >
                <BrandLogo company={s.company} logo={s.logo} size={16} />
                <span className="text-[11px] text-white/65 truncate max-w-[80px]">
                  {s.company}
                </span>
              </div>
            ))}
            {additionalSponsors.length > 4 && (
              <div className="flex items-center px-2 py-1 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                <span className="text-[11px] text-white/45">
                  +{additionalSponsors.length - 4} more
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table sponsor slots */}
      {e.tables_total > 0 && (
        <div data-testid={`calendar-table-summary-${e.id}`}>
          <p className="overline mb-2 flex items-center gap-1.5 text-[10px]">
            <Users size={10} aria-hidden="true" /> Sponsor table slots
          </p>

          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span
              className={`text-xs px-2.5 py-1 rounded-full border ${
                hasOpenTables
                  ? "border-[#C6A87C]/50 text-[#C6A87C] bg-[#C6A87C]/[0.06]"
                  : "border-white/15 text-white/55"
              }`}
            >
              {hasOpenTables
                ? `${e.tables_open} open slot${e.tables_open === 1 ? "" : "s"}`
                : "All slots claimed"}
            </span>

            {hasOpenTables && (
              <Link
                to={`/sponsor?event=${encodeURIComponent(e.id)}`}
                data-testid={`calendar-claim-table-${e.id}`}
                className="text-xs text-white/75 hover:text-[#C6A87C] flex items-center gap-1 transition-colors"
              >
                Claim a table slot <ArrowRight size={11} />
              </Link>
            )}
          </div>

          <ul className="space-y-1.5">
            {visibleSlots.map((t) => (
              <li
                key={t.table_id}
                className="text-xs text-white/65 flex items-center justify-between gap-2"
              >
                <span className="truncate flex items-center gap-1.5">
                  {t.status === "claimed" && t.sponsor_logo && (
                    <BrandLogo
                      company={t.sponsor_name}
                      logo={t.sponsor_logo}
                      size={14}
                    />
                  )}
                  <span className="truncate">{t.circle || "Table"}</span>
                </span>
                <span
                  className={`shrink-0 text-[10px] uppercase tracking-wider whitespace-nowrap ${
                    t.status === "open" ? "text-[#C6A87C]" : "text-white/40"
                  }`}
                >
                  {t.status === "open" ? "Open" : "Claimed"}
                </span>
              </li>
            ))}
            {e.table_slots.length > 5 && (
              <li className="text-[11px] text-white/40 italic">
                +{e.table_slots.length - 5} more slots
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Description snippet */}
      {e.description && (
        <p className="text-xs text-white/50 leading-relaxed line-clamp-2">
          {e.description}
        </p>
      )}

      {/* Footer */}
      <footer className="mt-auto flex items-center gap-3 pt-3 border-t border-white/[0.06]">
        <Link
          to={`/events/${e.id}`}
          className="text-xs text-white/70 hover:text-[#C6A87C] flex items-center gap-1 transition-colors"
          data-testid={`calendar-detail-${e.id}`}
        >
          See agenda <ArrowRight size={11} />
        </Link>

        {(!claimed || hasOpenTables) && (
          <Link
            to={`/sponsor?event=${encodeURIComponent(e.id)}`}
            className="text-xs px-3 py-1.5 rounded-full bg-[#C6A87C] text-[#061412] hover:bg-[#D9BC93] transition-colors ml-auto font-medium"
            data-testid={`calendar-sponsor-${e.id}`}
          >
            Sponsor this
          </Link>
        )}

        {claimed && !hasOpenTables && (
          <span className="text-[10px] uppercase tracking-wider text-white/40 ml-auto flex items-center gap-1">
            <CheckCircle2 size={11} aria-hidden="true" /> Fully sponsored
          </span>
        )}
      </footer>
    </article>
  );
});

// ---------------------------------------------------------------------------
// EVENT GRID
// ---------------------------------------------------------------------------

const EventGrid = memo(
  ({ grouped, loading, error, onRetry, onClearFilters }) => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {Array.from({ length: 4 }, (_, i) => (
            <CalendarCardSkeleton key={i} />
          ))}
        </div>
      );
    }

    if (error) {
      return <ErrorState message={error} onRetry={onRetry} />;
    }

    const hasBuckets = BUCKET_ORDER.some(
      (bk) => (grouped[bk] || []).length > 0,
    );

    if (!hasBuckets) {
      return <EmptyState onClear={onClearFilters} />;
    }

    return (
      <>
        {BUCKET_ORDER.map((bk) => {
          const rows = grouped[bk] || [];
          if (rows.length === 0) return null;
          return (
            <div key={bk} className="mb-12" data-testid={`bucket-${bk}`}>
              <p className="overline mb-5">
                {bk} · {rows.length}
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {rows.map((e) => (
                  <CalendarCard key={e.id} event={e} />
                ))}
              </div>
            </div>
          );
        })}
      </>
    );
  },
);
EventGrid.displayName = "EventGrid";

// ---------------------------------------------------------------------------
// REGISTRY FOOTER — static, memoised
// ---------------------------------------------------------------------------

const RegistryFooter = memo(() => (
  <div
    className="glass rounded-2xl p-8 md:p-10 mt-12 grid md:grid-cols-12 gap-6 items-center"
    data-testid="calendar-registry-footer"
  >
    <div className="md:col-span-8">
      <p className="overline mb-3">Don't see the right event?</p>
      <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight leading-tight">
        Join the registry — we'll notify you the moment a matching slot opens.
      </h2>
    </div>
    <div className="md:col-span-4 md:text-right">
      <Link
        to="/sponsor-registry"
        className="btn-gold"
        data-testid="calendar-registry-cta"
      >
        Join the registry <ArrowRight size={16} />
      </Link>
    </div>
  </div>
));
RegistryFooter.displayName = "RegistryFooter";

// ---------------------------------------------------------------------------
// MAIN PAGE
// ---------------------------------------------------------------------------

export default function SponsorshipCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("open");
  const [tagFilter, setTagFilter] = useState(null);

  const abortRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    setLoading(true);
    setError(null);

    try {
      const res = await getAllEvents();
      if (signal.aborted) return;

      // Handle both { success, data: [...] } and { success, data: { data: [...] } }
      const rawList =
        res?.data?.data || res?.data || (Array.isArray(res) ? res : []);

      if (!Array.isArray(rawList)) {
        setEvents([]);
        return;
      }

      const normalized = rawList
        .filter((e) => !e.isDeleted)
        .map(normalizeEventForCalendar)
        .sort(
          (a, b) =>
            new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
        );

      setEvents(normalized);
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("SponsorshipCalendar fetch error:", err);
      setError("Failed to load the sponsorship calendar. Please try again.");
      toast.error("Could not load events.");
    } finally {
      if (!abortRef.current?.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  // Build available tag filters dynamically from the actual event data
  const availableTags = useMemo(() => {
    const tagSet = new Set();
    events.forEach((e) => {
      e.tags.forEach((t) => {
        if (t && t !== "Local" && t !== "Global") tagSet.add(t);
      });
    });
    return Array.from(tagSet).sort();
  }, [events]);

  const openCount = useMemo(
    () =>
      events.filter((e) => e.event_slot.status === "open" || e.tables_open > 0)
        .length,
    [events],
  );

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (filter === "open") {
        if (e.event_slot.status !== "open" && e.tables_open <= 0) return false;
      }
      if (tagFilter) {
        const matches = e.tags.some(
          (t) => t.toLowerCase() === tagFilter.toLowerCase(),
        );
        if (!matches) return false;
      }
      return true;
    });
  }, [events, filter, tagFilter]);

  const grouped = useMemo(() => {
    const out = {};
    for (const e of filtered) {
      const k = bucketFor(e.start_time);
      (out[k] = out[k] || []).push(e);
    }
    return out;
  }, [filtered]);

  const icsUrl = useMemo(() => {
    const params = new URLSearchParams({ days: "60" });
    if (tagFilter) params.set("topic", tagFilter);
    if (filter === "all") params.set("only_open", "false");
    return `${BACKEND}/api/sponsorship-calendar.ics?${params.toString()}`;
  }, [tagFilter, filter]);

  const webcalUrl = useMemo(
    () => icsUrl.replace(/^https?:\/\//, "webcal://"),
    [icsUrl],
  );

  // ---------------------------------------------------------------------------
  // Stable handlers
  // ---------------------------------------------------------------------------
  const handleFilterChange = useCallback((key) => setFilter(key), []);
  const handleTagChange = useCallback((tag) => setTagFilter(tag), []);

  const clearFilters = useCallback(() => {
    setFilter("all");
    setTagFilter(null);
  }, []);

  const copySubscribeUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(icsUrl);
      toast.success(
        "Calendar link copied. Paste into Google / Outlook / Apple Calendar.",
      );
    } catch {
      toast.error("Could not copy — try long-pressing the link.");
    }
  }, [icsUrl]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div data-testid="sponsorship-calendar-page" className="min-h-screen">
      {/* Hero */}
      <section
        className="border-b border-white/[0.06]"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="container-x py-16 md:py-20">
          <p className="overline mb-4 flex items-center gap-2">
            <Calendar size={12} aria-hidden="true" /> Sponsorship calendar
          </p>

          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <h1 className="font-display font-light text-4xl md:text-6xl tracking-tighter leading-[0.95]">
                The next 60 days —
                <br />
                <span className="italic text-[#C6A87C]">
                  live availability per event.
                </span>
              </h1>
              {/* Live counts */}
              {!loading && events.length > 0 && (
                <div className="mt-6 flex items-center gap-6 flex-wrap">
                  <div>
                    <span className="font-display text-3xl text-[#C6A87C]">
                      {events.length}
                    </span>
                    <span className="text-sm text-white/60 ml-2">events</span>
                  </div>
                  <div>
                    <span className="font-display text-3xl text-[#7BC8A6]">
                      {openCount}
                    </span>
                    <span className="text-sm text-white/60 ml-2">
                      with open slots
                    </span>
                  </div>
                  <div>
                    <span className="font-display text-3xl text-white/80">
                      {events
                        .reduce((sum, e) => sum + e.registered, 0)
                        .toLocaleString()}
                    </span>
                    <span className="text-sm text-white/60 ml-2">
                      total registrations
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="md:col-span-4">
              <p className="text-white/80 leading-relaxed">
                Every vmeetio event with its event-level and table-level sponsor
                slots, in one place. Click any open slot to claim it, or{" "}
                <Link
                  to="/sponsor-registry"
                  className="text-[#C6A87C] hover:underline"
                >
                  join the registry
                </Link>{" "}
                to be notified the moment one opens.
              </p>

              <SubscribePanel
                icsUrl={icsUrl}
                webcalUrl={webcalUrl}
                onCopy={copySubscribeUrl}
              />
            </div>
          </div>

          <FilterControls
            filter={filter}
            onFilterChange={handleFilterChange}
            tagFilter={tagFilter}
            onTagChange={handleTagChange}
            openCount={openCount}
            totalCount={events.length}
            availableTags={availableTags}
          />
        </div>
      </section>

      {/* Grid */}
      <section className="section">
        <div className="container-x">
          <EventGrid
            grouped={grouped}
            loading={loading}
            error={error}
            onRetry={fetchData}
            onClearFilters={clearFilters}
          />
          <RegistryFooter />
        </div>
      </section>
    </div>
  );
}
